import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

interface PaginationProps {
  page: number
  pageCount: number
  pageSize?: number
  total?: number
  onPage: (page: number) => void
  onPageSize?: (pageSize: number) => void
  className?: string
}

function clampPage(page: number, pageCount: number) {
  const safePageCount = Math.max(1, pageCount)
  return Math.min(Math.max(0, page), safePageCount - 1)
}

function pageRange(page: number, pageSize?: number, total?: number) {
  if (!pageSize || total === undefined) return null
  if (total <= 0) return { start: 0, end: 0, total: 0 }

  const start = page * pageSize + 1
  const end = Math.min(total, start + pageSize - 1)
  return { start, end, total }
}

export function Pagination({
  page,
  pageCount,
  pageSize,
  total,
  onPage,
  onPageSize,
  className,
}: PaginationProps) {
  const { t } = useTranslation()
  const safePageCount = Math.max(1, pageCount)
  const safePage = clampPage(page, safePageCount)
  const range = pageRange(safePage, pageSize, total)
  const canPrevious = safePage > 0
  const canNext = safePage < safePageCount - 1
  const pageSizeOptions = pageSize
    ? Array.from(new Set([...DEFAULT_PAGE_SIZE_OPTIONS, pageSize])).sort((a, b) => a - b)
    : DEFAULT_PAGE_SIZE_OPTIONS

  return (
    <div className={cn('flex flex-col gap-3 pt-3 sm:flex-row sm:items-center sm:justify-between', className)}>
      <div className="text-sm text-muted-foreground">
        {range
          ? range.start === range.end
            ? t('pagination.single', { index: range.start, total: range.total })
            : t('pagination.range', { start: range.start, end: range.end, total: range.total })
          : t('pagination.page', { page: safePage + 1, total: safePageCount })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {onPageSize && pageSize ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{t('pagination.pageSize')}</span>
            <Select value={String(pageSize)} onValueChange={(value) => onPageSize(Number(value))}>
              <SelectTrigger size="sm" className="w-[84px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label={t('pagination.first')}
            disabled={!canPrevious}
            onClick={() => onPage(0)}
          >
            <ChevronsLeft className="size-4" />
            <span className="sr-only">{t('pagination.first')}</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label={t('pagination.previous')}
            disabled={!canPrevious}
            onClick={() => onPage(safePage - 1)}
          >
            <ChevronLeft className="size-4" />
            <span className="sr-only">{t('pagination.previous')}</span>
          </Button>
          <span className="nf-mono min-w-16 px-2 text-center text-sm text-muted-foreground">
            {t('pagination.page', { page: safePage + 1, total: safePageCount })}
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label={t('pagination.next')}
            disabled={!canNext}
            onClick={() => onPage(safePage + 1)}
          >
            <ChevronRight className="size-4" />
            <span className="sr-only">{t('pagination.next')}</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label={t('pagination.last')}
            disabled={!canNext}
            onClick={() => onPage(safePageCount - 1)}
          >
            <ChevronsRight className="size-4" />
            <span className="sr-only">{t('pagination.last')}</span>
          </Button>
        </div>
      </div>
    </div>
  )
}
